import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ToastViewport from "../components/ui/toast-stack";
import { ToastMsgInit } from "../components/common/ToastMsg";

const ToastContext = createContext(null);

const MAX_VISIBLE_TOASTS = 5;
const DEFAULT_DURATION_MS = 4000;
const NOTIFICATION_DURATION_MS = 6500;

const createToastId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const ToastBridge = () => {
  const { showToast } = useToast();

  useEffect(() => {
    ToastMsgInit(showToast);
  }, [showToast]);

  return null;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const clearToastTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissToast = useCallback(
    (id) => {
      clearToastTimer(id);
      setToasts((prev) =>
        prev.map((item) => (item.id === id ? { ...item, exiting: true } : item))
      );
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== id));
      }, 180);
    },
    [clearToastTimer]
  );

  const showToast = useCallback(
    (type, text, options = {}) => {
      const id = createToastId();
      const isNotification = options.variant === "notification";
      const duration =
        options.duration ??
        (isNotification ? NOTIFICATION_DURATION_MS : DEFAULT_DURATION_MS);

      const toast = {
        id,
        type: type || "info",
        message: text,
        exiting: false,
        ...options,
      };

      setToasts((prev) => [toast, ...prev].slice(0, MAX_VISIBLE_TOASTS));

      if (duration > 0) {
        const timer = window.setTimeout(() => dismissToast(id), duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismissToast]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastBridge />
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};
